import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, CartOpenedEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#lockScroll);
    this.addEventListener(DialogCloseEvent.eventName, this.#unlockScroll);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#lockScroll);
    this.removeEventListener(DialogCloseEvent.eventName, this.#unlockScroll);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #lockScroll = () => {
    this.refs.dialog?.addEventListener('wheel', this.#preventScrollThrough, { passive: false });
  };

  #unlockScroll = () => {
    this.refs.dialog?.removeEventListener('wheel', this.#preventScrollThrough);
  };

  #preventScrollThrough = (/** @type {WheelEvent} */ event) => {
    const rect = this.refs.dialog.getBoundingClientRect();
    if (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    ) return;

    let el = /** @type {HTMLElement | null} */ (event.target);
    while (el && el !== this.refs.dialog) {
      if (el.scrollHeight > el.clientHeight + 1) return;
      el = el.parentElement;
    }
    event.preventDefault();
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });

    this.dispatchEvent(
      new CartOpenedEvent({}, this.sectionId, {
        source: 'cart-drawer',
      })
    );
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
